const PROMOTION_STORAGE_KEY = "ickonic.billing.promo";
const PROMOTION_CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function normalizePromotionCode(value: string | null | undefined): string | null {
    const normalized = value?.trim() ?? "";
    return PROMOTION_CODE_PATTERN.test(normalized) ? normalized : null;
}

function storage(): Storage | null {
    try {
        return typeof window === "undefined" ? null : window.sessionStorage;
    } catch {
        return null;
    }
}

export function rememberPromotionCode(value: string | null | undefined): string | null {
    const normalized = normalizePromotionCode(value);
    const session = storage();

    try {
        if (normalized) session?.setItem(PROMOTION_STORAGE_KEY, normalized);
        else session?.removeItem(PROMOTION_STORAGE_KEY);
    } catch {
        // Storage can be disabled by the browser. The URL still works for this page.
    }

    return normalized;
}

export function clearRememberedPromotionCode(): void {
    try {
        storage()?.removeItem(PROMOTION_STORAGE_KEY);
    } catch {
        // No action is needed when storage is unavailable.
    }
}

export function clearPromotionCode(): void {
    clearRememberedPromotionCode();
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    if (!params.has("promo")) return;
    params.delete("promo");
    const query = params.toString();
    window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    );
}

/**
 * Captures an explicit `?promo=` value and otherwise returns the value retained
 * for this tab. This lets a targeted link survive registration, authentication,
 * payment recovery and internal navigation without leaking into another tab.
 */
export function getPromotionCode(search?: string): string | null {
    const effectiveSearch = search ?? (typeof window === "undefined" ? "" : window.location.search);
    const params = new URLSearchParams(effectiveSearch);

    if (params.has("promo")) {
        return rememberPromotionCode(params.get("promo"));
    }

    try {
        return normalizePromotionCode(storage()?.getItem(PROMOTION_STORAGE_KEY));
    } catch {
        return null;
    }
}

export function withPromotionCode(url: string, promotionCode = getPromotionCode()): string {
    const code = normalizePromotionCode(promotionCode);
    if (!code) return url;

    const hashIndex = url.indexOf("#");
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
    const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const queryIndex = withoutHash.indexOf("?");
    const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
    const params = new URLSearchParams(queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : "");
    params.set("promo", code);
    return `${path}?${params.toString()}${hash}`;
}
